import React, { useState, useRef } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  makeStyles,
  IconButton,
  Typography,
  Box,
  FormControl,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  Divider
} from '@material-ui/core'
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  ErrorOutline as ErrorOutlineIcon,
  InfoRounded,
  Info as InfoIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { toast } from 'utils/notify'
import { downloadSpeakerSampleCSV } from 'utils/downloadCSV'
import { parse } from 'papaparse'
import { capitalize, isValidEmail, parseCsvData } from 'utils'
import { creationType, SpeakerRoles, SubjectStatus } from 'constants/index'
import { nanoid } from 'nanoid'
import { bulkInsertSpeakers, scanImportedSpeakers } from 'store/speaker'
import { cloudFunctionsInstance } from 'config/axios'
import { CountNumber } from '../../organizations/components/ImportSummaryModal'

const ImportSpeakersModal = ({ open, onClose }) => {
  const classes = useStyles()
  const inputFileRef = useRef()
  const [csvFile, setCsvFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [csvError, setCsvError] = useState('')
  const [csvInfoMsg, setCsvInfoMsg] = useState('')

  // New state variables for review functionality
  const [showReviewTable, setShowReviewTable] = useState(false)
  const [parsedData, setParsedData] = useState([])
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [importSummary, setImportSummary] = useState({
    uniqueCount: 0,
    existingCount: 0,
    invalidCount: 0,
    foundInConfCount: 0
  })

  const [duplicateCount, setDuplicateCount] = useState(0)

  // Dropdown menu and confirmation state
  const [anchorEl, setAnchorEl] = useState(null)
  const [showApprovedConfirmation, setShowApprovedConfirmation] = useState(false)

  const getRowErrors = (row) => {
    const errors = new Set()
    if (!row.firstName) errors.add('firstName')
    if (!row.lastName) errors.add('lastName')
    if (!row.email || !isValidEmail(row.email)) errors.add('email')
    return errors
  }

  const { user } = useSelector((state) => state.auth)
  const { conference } = useSelector((state) => state.conference)

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.csv')) {
        setCsvError('Please upload a CSV file.')
        setCsvFile(null)
        return
      }
      setCsvFile(file)
      parse(file, {
        header: false,
        skipEmptyLines: 'greedy',
        complete: async (results) => {
          const data = results.data
          const rawRows = parseCsvData(data).map((item) => ({
            ...item,
            email: item.email ? item.email.toLowerCase().trim() : item.email
          }))

          // Manual dedup: track seen emails, mark duplicates instead of dropping them
          const seenEmails = new Set()
          const validRoles = [SpeakerRoles.PRESENTER, SpeakerRoles.MODERATOR]
          const parsedRecords = rawRows.map((item) => {
            const roles = []

            if (item?.role) {
              const roleStrings = item.role.split(';').map((role) => role.trim())
              roleStrings.forEach((roleString) => {
                const normalizedRole = roleString.toLowerCase()
                if (validRoles.includes(normalizedRole) && !roles.includes(normalizedRole)) {
                  roles.push(normalizedRole)
                }
              })
            }

            if (roles.length === 0) {
              roles.push(SpeakerRoles.PRESENTER)
            }

            const emailKey = item.email ? item.email : null
            const isDuplicate = emailKey ? seenEmails.has(emailKey) : false
            if (emailKey && !isDuplicate) seenEmails.add(emailKey)

            return { ...item, roles, isDuplicate }
          })

          const dupCount = parsedRecords.filter((r) => r.isDuplicate).length
          const invalidCount = parsedRecords.filter((r) => getRowErrors(r).size > 0).length
          const validCount = parsedRecords.filter(
            (r) => !r.isDuplicate && getRowErrors(r).size === 0
          ).length

          if (validCount === 0 && dupCount === 0) {
            setCsvError('The CSV file must contain at least one valid record.')
            setShowReviewTable(false)
          } else {
            setCsvError('')
            setCsvInfoMsg('')
            setParsedData(parsedRecords)
            setShowReviewTable(true)
          }

          setDuplicateCount(dupCount)
          setImportSummary((prev) => ({
            ...prev,
            invalidCount
          }))
        },
        error: (error) => {
          setCsvError(error.message)
          setShowReviewTable(false)
        }
      })
    } else {
      setCsvFile(null)
      setCsvInfoMsg('')
      setShowReviewTable(false)
    }
    setCsvError('')
  }

  const handleSubmit = async () => {
    if (!csvFile) {
      setCsvError('Please select a CSV file to upload')
      return
    }

    setLoading(true)
    try {
      const speakersData = parsedData
        ?.filter((item) => !item.isDuplicate && getRowErrors(item).size === 0)
        .map((item) => ({
          firstName: item?.firstName ?? '',
          lastName: item?.lastName ?? '',
          fullName: `${item?.firstName} ${item?.lastName}` ?? '',
          email: item?.email ?? '',
          credential: item?.credential ?? '',
          position: item?.position ?? '',
          company: item?.organization ?? '',
          phone: item?.phone ?? '',
          city: item?.city ?? '',
          state: item?.state ?? '',
          country: item?.country ?? '',
          websiteUrl: item?.websiteUrl ?? '',
          bio: item?.bio ?? '',
          roles: item?.roles?.length ? item?.roles : [SpeakerRoles.PRESENTER],

          token: nanoid(),
          status: SubjectStatus.INVITE_NOT_SENT,
          creationType: creationType.INVITATION,
          conferenceId: user?.conferenceId
        }))

      await performImport(speakersData, false)
    } catch (error) {
      toast.error('Could not import speakers. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitApproved = async () => {
    if (!csvFile) {
      setCsvError('Please select a CSV file to upload')
      return
    }

    setLoading(true)
    try {
      const speakersData = parsedData
        ?.filter((item) => !item.isDuplicate && getRowErrors(item).size === 0)
        .map((item) => ({
          firstName: item?.firstName ?? '',
          lastName: item?.lastName ?? '',
          fullName: `${item?.firstName} ${item?.lastName}` ?? '',
          email: item?.email ?? '',
          credential: item?.credential ?? '',
          position: item?.position ?? '',
          company: item?.organization ?? '',
          phone: item?.phone ?? '',
          city: item?.city ?? '',
          state: item?.state ?? '',
          country: item?.country ?? '',
          websiteUrl: item?.websiteUrl ?? '',
          bio: item?.bio ?? '',
          roles: item?.roles?.length ? item?.roles : [SpeakerRoles.PRESENTER],

          token: null,
          status: SubjectStatus.APPROVED,
          creationType: creationType.MANUAL,
          conferenceId: user?.conferenceId
        }))

      await performImport(speakersData, true)
    } catch (error) {
      toast.error('Could not import speakers. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const performImport = async (speakersData, isApproved = false) => {
    let brandNewSpeakers = speakersData
    let existingSpeakersInXConference = []
    let xSpeakersToBeIncludedInCurrentConf = []

    // Skip scanning previous conference for approved imports
    if (!isApproved) {
      const scanResult = await scanImportedSpeakers(speakersData, user?.conferenceId)
      brandNewSpeakers = scanResult.brandNewSpeakers
      existingSpeakersInXConference = scanResult.existingSpeakers
    }

    const previousConferenceId = conference?.previousConferenceId
    const { uniqueCount, existingCount, existingSpeakerEmails } = await bulkInsertSpeakers(
      [...brandNewSpeakers, ...existingSpeakersInXConference],
      conference?.id,
      previousConferenceId
    )

    // Filter existing speakers and send notifications only for non-approved imports
    if (!isApproved && existingSpeakersInXConference?.length) {
      xSpeakersToBeIncludedInCurrentConf = existingSpeakersInXConference?.filter(
        (item) => !existingSpeakerEmails?.includes?.(item.email)
      )

      if (xSpeakersToBeIncludedInCurrentConf?.length) {
        try {
          await cloudFunctionsInstance.post('/notifyImportedSpeakersToUpdateProfile', {
            conferenceId: conference?.id
          })
        } catch (err) {
          console.error('Error sending notification email:', err)
          toast.error('Could not send notification emails. Please try again.')
        }
      }
    }

    // Update import summary data
    setImportSummary({
      uniqueCount: uniqueCount,
      existingCount: existingCount,
      invalidCount: importSummary.invalidCount || 0,
      foundInConfCount: xSpeakersToBeIncludedInCurrentConf?.length || 0
    })
    setShowSummaryModal(true)
    setCsvFile(null)
    setCsvInfoMsg('')
    setCsvError('')
    setShowReviewTable(false)
    setParsedData([])
    setDuplicateCount(0)
    setAnchorEl(null)
    setShowApprovedConfirmation(false)
    onClose()
  }

  const handleDropdownClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleDropdownClose = () => {
    setAnchorEl(null)
  }

  const handleImportToInvite = () => {
    setAnchorEl(null)
    handleSubmit()
  }

  const handleImportToApproved = () => {
    setAnchorEl(null)
    setShowApprovedConfirmation(true)
  }

  const handleConfirmApprovedImport = () => {
    setShowApprovedConfirmation(false)
    handleSubmitApproved()
  }

  const handleCancelApprovedImport = () => {
    setShowApprovedConfirmation(false)
  }

  const handleClose = () => {
    setCsvFile(null)
    setCsvError('')
    setCsvInfoMsg('')
    setShowReviewTable(false)
    setParsedData([])
    setDuplicateCount(0)
    setAnchorEl(null)
    setShowApprovedConfirmation(false)
    onClose()
  }

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false)
    setImportSummary({
      uniqueCount: 0,
      existingCount: 0,
      invalidCount: 0
    })
    onClose()
  }

  return (
    <>
      <Dialog open={open} maxWidth="xxl" fullWidth>
        <DialogTitle className={classes.dialogTitle}>
          Import Speakers via CSV
          <IconButton className={classes.closeButton} onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {/* Step 1: Info Box */}
          <Box className={classes.note}>
            <Box className={classes.noteHeader}>
              <Typography className={classes.noteHead}>Instructions:</Typography>
            </Box>
            <Box className={classes.noteSteps}>
              <Box className={classes.stepItem}>
                <Box className={classes.stepNumber}>1</Box>
                <Typography className={classes.stepText}>
                  Upload a CSV file with speaker information. All speakers will be created with
                  status <span style={{ fontWeight: 'bold' }}>&quot;Invite not sent&quot;</span>.
                </Typography>
              </Box>

              <Box className={classes.fileFormatInfo}>
                <Box>
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                    <Typography variant="subtitle2" className={classes.formatTitle}>
                      Sample CSV Format
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      className={classes.sampleDownloadButton}
                      onClick={downloadSpeakerSampleCSV}
                      startIcon={<DownloadIcon />}>
                      Download Sample CSV
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.formatHeaderCell}>
                            First Name <span className={classes.required}>*</span>
                          </TableCell>
                          <TableCell className={classes.formatHeaderCell}>
                            Last Name <span className={classes.required}>*</span>
                          </TableCell>
                          <TableCell className={classes.formatHeaderCell}>
                            Email <span className={classes.required}>*</span>
                          </TableCell>
                          <TableCell className={classes.formatHeaderCell}>Role</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Credential</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Position</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Organization</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Phone</TableCell>
                          <TableCell className={classes.formatHeaderCell}>City</TableCell>
                          <TableCell className={classes.formatHeaderCell}>State</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Country</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Website URL</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Bio</TableCell>
                        </TableRow>
                      </TableHead>
                    </Table>
                  </TableContainer>

                  <Box className={classes.roleInfoBox}>
                    <InfoIcon className={classes.roleInfoIcon} />
                    <Typography className={classes.roleInfoText}>
                      {`Role must be either 'Presenter' or 'Moderator'. For multiple roles, separate them with semicolons (e.g., 'Presenter; Moderator'). If left blank or any invalid value is provided, it will default to 'Presenter'.`}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box className={classes.stepItem}>
                <Box className={classes.stepNumber}>2</Box>
                <Typography className={classes.stepText}>
                  We&apos;ll scan for speakers from previous conferences. If found, we&apos;ll
                  retrieve their information and send an email for profile review. These speakers
                  will be moved to{' '}
                  <span style={{ fontWeight: 'bold' }}>&quot;Pending data review&quot;</span>{' '}
                  status.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Step 3: File Upload */}
          <Box style={{ marginTop: '24px' }}>
            <FormControl error={Boolean(csvError)} fullWidth>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  ref={inputFileRef}
                  className={classes.input}
                  disabled={loading}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  className={classes.upload}
                  onClick={() => inputFileRef.current.click()}
                  disabled={loading}
                  startIcon={<CloudUploadIcon />}>
                  {csvFile ? csvFile?.name : 'Select a CSV file'}
                </Button>
                {csvFile && (
                  <IconButton
                    size="small"
                    title="Clear selected file"
                    disabled={loading}
                    onClick={() => {
                      setCsvFile(null)
                      setCsvError('')
                      setCsvInfoMsg('')
                      setShowReviewTable(false)
                      setParsedData([])
                      setDuplicateCount(0)
                      setImportSummary((prev) => ({ ...prev, invalidCount: 0 }))
                      if (inputFileRef.current) inputFileRef.current.value = null
                    }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </FormControl>
            {csvError && (
              <Box className={classes.errorBox}>
                <ErrorOutlineIcon className={classes.errorIcon} />
                <FormHelperText className={classes.error}>{csvError}</FormHelperText>
              </Box>
            )}
            {csvInfoMsg && (
              <Box className={classes.infoBox}>
                <InfoRounded className={classes.infoIcon} />
                <FormHelperText className={classes.info}>{csvInfoMsg}</FormHelperText>
              </Box>
            )}
          </Box>

          {/* Step 4: Review Data Table */}
          {showReviewTable && parsedData.length > 0 && (
            <Box className={classes.tableContainer}>
              <Typography className={classes.tableTitle}>
                Review Speakers Data ({parsedData.length})
              </Typography>
              {importSummary.invalidCount > 0 && (
                <Box className={classes.warningBanner}>
                  <InfoRounded className={classes.warningIcon} />
                  <Typography className={classes.warningText}>
                    <strong>
                      {importSummary.invalidCount} row{importSummary.invalidCount !== 1 ? 's' : ''}{' '}
                      have invalid or missing required fields.
                    </strong>{' '}
                    These rows will be <strong>skipped</strong> during import — only valid rows will
                    be imported. You can also fix the CSV and re-upload.
                  </Typography>
                </Box>
              )}
              {duplicateCount > 0 && (
                <Box className={classes.duplicateBanner}>
                  <InfoRounded className={classes.duplicateIcon} />
                  <Typography className={classes.duplicateText}>
                    <strong>
                      {duplicateCount} duplicate email{duplicateCount !== 1 ? 's' : ''} found.
                    </strong>{' '}
                    Duplicate rows will be <strong>automatically skipped</strong> — only the first
                    occurrence of each email is kept.
                  </Typography>
                </Box>
              )}
              {importSummary.invalidCount === 0 && duplicateCount === 0 && (
                <Box className={classes.importInfo}>
                  <Box className={classes.infoNumberCircle}>i</Box>
                  <Typography className={classes.importInfoText}>
                    Please review the data before importing. Only speakers with valid information
                    will be imported.
                  </Typography>
                </Box>
              )}

              <TableContainer component={Paper} className={classes.reviewTable}>
                <Table size="small">
                  <TableHead className={classes.reviewHeader}>
                    <TableRow>
                      <TableCell className={classes.reviewHeaderCell}>First Name</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Last Name</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Email</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Role</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Credential</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Position</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Organization</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Phone</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>City</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>State</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Country</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Website URL</TableCell>
                      <TableCell className={classes.reviewHeaderCell}>Bio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedData?.map((row, index) => {
                      const errors = getRowErrors(row)
                      const rowInvalid = errors.size > 0
                      const borderColor = rowInvalid
                        ? '#f44336'
                        : row.isDuplicate
                        ? '#ff9800'
                        : 'transparent'
                      return (
                        <TableRow
                          key={index}
                          style={
                            rowInvalid || row.isDuplicate
                              ? { borderLeft: `3px solid ${borderColor}` }
                              : {}
                          }>
                          <TableCell
                            className={`${classes.reviewCell} ${
                              errors.has('firstName') ? classes.cellError : ''
                            }`}>
                            {row.firstName || <span className={classes.missingValue}>Missing</span>}
                          </TableCell>
                          <TableCell
                            className={`${classes.reviewCell} ${
                              errors.has('lastName') ? classes.cellError : ''
                            }`}>
                            {row.lastName || <span className={classes.missingValue}>Missing</span>}
                          </TableCell>
                          <TableCell
                            className={`${classes.reviewCell} ${
                              errors.has('email')
                                ? classes.cellError
                                : row.isDuplicate
                                ? classes.cellDuplicate
                                : ''
                            }`}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {row.email || <span className={classes.missingValue}>Missing</span>}
                              {row.isDuplicate && (
                                <span className={classes.duplicateBadge}>Duplicate</span>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell className={classes.reviewCell}>
                            {row.roles?.length > 0
                              ? row.roles.map((role) => capitalize(role)).join(', ')
                              : '-'}
                          </TableCell>
                          <TableCell className={classes.reviewCell}>
                            {row.credential || '-'}
                          </TableCell>
                          <TableCell className={classes.reviewCell}>
                            {row.position || '-'}
                          </TableCell>
                          <TableCell className={classes.reviewCell}>
                            {row.organization || '-'}
                          </TableCell>
                          <TableCell className={classes.reviewCell}>{row.phone || '-'}</TableCell>
                          <TableCell className={classes.reviewCell}>{row.city || '-'}</TableCell>
                          <TableCell className={classes.reviewCell}>{row.state || '-'}</TableCell>
                          <TableCell className={classes.reviewCell}>{row.country || '-'}</TableCell>
                          <TableCell className={classes.reviewCell}>
                            {row.websiteUrl || '-'}
                          </TableCell>
                          <TableCell className={classes.reviewCell}>{row.bio || '-'}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleClose} disabled={loading} className={classes.cancelButton}>
            Cancel
          </Button>
          <Box className={classes.importButtonContainer}>
            <Button
              variant="contained"
              color="primary"
              disabled={loading || !csvFile}
              onClick={handleDropdownClick}
              endIcon={<ArrowDropDownIcon />}
              className={classes.importDropdownButton}>
              {loading ? 'Importing...' : 'Import Speakers'}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleDropdownClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}>
              <MenuItem onClick={handleImportToInvite} className={classes.menuItem}>
                <Box>
                  <Typography className={classes.menuItemTitle}>Import to Invite Status</Typography>
                  <Typography className={classes.menuItemDescription}>
                    Speakers will be imported with &quot;Invite not sent&quot; status
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleImportToApproved} className={classes.menuItem}>
                <Box>
                  <Typography className={classes.menuItemTitle}>
                    Import to Approved Status
                  </Typography>
                  <Typography className={classes.menuItemDescription}>
                    Speakers will be imported as &quot;Approved&quot; (no invites sent)
                  </Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Approved Import */}
      <Dialog
        open={showApprovedConfirmation}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: classes.confirmationDialogPaper
        }}>
        <DialogTitle className={classes.confirmationDialogTitle}>
          <Box className={classes.confirmationTitleContainer}>
            <Typography variant="h6">Confirm Import to Approved Status</Typography>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.confirmationDialogContent}>
          <Typography className={classes.confirmationMessage}>
            You are about to import <strong>{parsedData.length}</strong> speakers directly to
            <strong> &quot;Approved&quot;</strong> status.
          </Typography>
          <Box className={classes.confirmationWarningBox}>
            <Typography className={classes.confirmationWarning}>
              <strong>Important:</strong> These speakers will bypass the invitation workflow
              entirely. No invitation emails will be sent, and they will be immediately available
              for events/subevents/talks/posters assignments.
            </Typography>
          </Box>
          <Typography className={classes.confirmationQuestion}>
            Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions className={classes.confirmationDialogActions}>
          <Button
            onClick={handleCancelApprovedImport}
            disabled={loading}
            className={classes.confirmationCancelButton}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmApprovedImport}
            disabled={loading}
            variant="contained"
            color="primary"
            className={classes.confirmationConfirmButton}>
            {loading ? 'Importing...' : 'Import to Approved'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Summary Modal */}
      <Dialog
        open={showSummaryModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 3,
          className: classes.dialogPaper
        }}>
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant="h6" className={classes.modalTitle}>
            Import Summary
          </Typography>
          <IconButton className={classes.closeButton} onClick={handleCloseSummaryModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box>
            <Box className={classes.summaryBox}>
              <Box className={classes.summaryItem}>
                <Box className={classes.summaryIconSuccess}>
                  <CountNumber
                    value={importSummary.uniqueCount}
                    className={classes.summaryNumber}
                  />
                </Box>
                <Box className={classes.summaryTextContainer}>
                  <Typography className={classes.summaryText}>
                    Speaker(s) successfully imported
                  </Typography>
                </Box>
              </Box>

              <Box className={classes.summaryItem}>
                <Box className={classes.summaryIconSkipped}>
                  <CountNumber
                    value={importSummary.existingCount}
                    className={classes.summaryNumber}
                  />
                </Box>
                <Box className={classes.summaryTextContainer}>
                  <Typography className={classes.summaryText}>
                    Speaker(s) skipped due to existing email addresses
                  </Typography>
                </Box>
              </Box>

              {importSummary?.foundInConfCount ? (
                <Box className={classes.summaryItem}>
                  <Box className={classes.summaryIconSkipped}>
                    <CountNumber
                      value={importSummary?.foundInConfCount}
                      className={classes.summaryNumber}
                    />
                  </Box>
                  <Box className={classes.summaryTextContainer}>
                    <Typography className={classes.summaryText}>
                      Speaker profiles were found from previous conference and moved to{' '}
                      <span style={{ fontWeight: 'bold' }}>&quot;Pending data review&quot;</span>.
                      An email has also been sent to these speakers to review and update their
                      profiles.
                    </Typography>
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className={classes.buttonBox}>
          <Button
            onClick={handleCloseSummaryModal}
            variant="contained"
            color="primary"
            className={classes.okButton}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ImportSpeakersModal

const useStyles = makeStyles((theme) => ({
  roleInfoBox: {
    marginTop: '16px',
    backgroundColor: 'rgba(255, 193, 7, 0.1)',
    padding: '12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    borderLeft: '4px solid #ffc107'
  },
  roleInfoIcon: {
    color: '#f57c00',
    marginTop: '3px',
    fontSize: '18px'
  },
  roleInfoText: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#5d4037',
    lineHeight: '1.5'
  },
  dialogTitle: {
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& h2': {
      fontWeight: 600,
      fontSize: '20px'
    }
  },
  upload: {
    textTransform: 'none',
    flex: 1,
    justifyContent: 'flex-start',
    padding: '14px 16px',
    fontSize: '14px',
    fontWeight: 500,
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    background: '#FFFFFF',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#03a9f4',
      boxShadow: '0 2px 5px rgba(3, 169, 244, 0.15)'
    }
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '1rem',
    marginBottom: '10px'
  },
  rowKey: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#455a64'
  },
  rowValue: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#03a9f4'
  },
  input: {
    display: 'none'
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px',
    marginLeft: '12px'
  },
  errorIcon: {
    fontSize: '18px',
    color: theme.palette.error.main,
    marginRight: '8px'
  },
  error: {
    marginTop: '0px',
    color: '#f44336',
    fontWeight: 500
  },
  info: {
    marginTop: '0',
    color: '#0288d1',
    fontWeight: 500
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  infoBoxColumn: {
    marginTop: theme.spacing(1.5),
    gap: theme.spacing(1),
    backgroundColor: 'rgba(3, 169, 244, 0.08)',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    border: '1px solid rgba(3, 169, 244, 0.2)'
  },
  infoBox: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(3, 169, 244, 0.08)',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    border: '1px solid rgba(3, 169, 244, 0.2)'
  },
  infoIcon: {
    color: theme.palette.info.main,
    fontSize: 20
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    color: '#2c3e50',
    fontSize: '16px'
  },
  csvFormat: {
    marginTop: theme.spacing(2),
    fontWeight: 'bold'
  },
  csvFormatBox: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(3),
    overflowX: 'auto',
    border: '1px solid #e0e0e0'
  },
  fileName: {
    color: theme.palette.primary.main
  },
  fileFormatInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.spacing(1),
    border: '1px solid #e0e0e0'
  },
  formatTags: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  chipTag: {
    backgroundColor: '#e3f2fd',
    color: '#0277bd',
    fontWeight: 500,
    border: '1px solid #90caf9'
  },
  optionalChip: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
    fontWeight: 500,
    border: '1px solid #ce93d8'
  },
  selectedFileBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: '#f1f8e9',
    borderRadius: theme.spacing(0.5),
    border: '1px solid #c5e1a5'
  },
  fileUpload: {
    marginTop: theme.spacing(2)
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  cancelButton: {
    marginRight: theme.spacing(1)
  },
  downloadButton: {
    marginLeft: theme.spacing(1)
  },
  sampleDownloadButton: {
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 500,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    borderColor: '#03a9f4',
    color: '#0277bd',
    '&:hover': {
      backgroundColor: '#e3f2fd',
      borderColor: '#0277bd',
      boxShadow: '0 2px 5px rgba(3, 169, 244, 0.15)'
    }
  },
  // New styles
  dialogPaper: {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
  },
  note: {
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: 'rgba(3, 169, 244, 0.08)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    marginBottom: '24px',
    border: '1px solid rgba(3, 169, 244, 0.2)'
  },
  noteHeader: {
    marginBottom: '16px'
  },
  noteHead: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#2c3e50',
    lineHeight: 1.5
  },
  noteSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#03a9f4',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
    flexShrink: 0
  },
  stepText: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#37474f',
    lineHeight: 1.6,
    paddingTop: '4px'
  },
  formatTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    color: '#455a64'
  },
  required: {
    fontWeight: 'bold',
    fontSize: '18px',
    color: '#f44336'
  },
  tableContainer: {
    marginTop: '24px',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0'
  },
  tableTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: '16px'
  },
  reviewTable: {
    marginTop: '16px',
    marginBottom: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    borderRadius: '8px',
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    maxHeight: '300px',
    position: 'relative'
  },
  reviewHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#f5f9ff'
  },
  reviewHeaderCell: {
    backgroundColor: '#f5f9ff',
    color: '#0277bd',
    fontWeight: 600,
    padding: '10px 12px',
    fontSize: '13px',
    borderBottom: '1px solid #e0e0e0'
  },
  reviewCell: {
    padding: '10px 12px',
    fontSize: '13px',
    color: '#455a64',
    borderBottom: '1px solid #f0f0f0'
  },
  importInfo: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f5f9ff',
    borderRadius: '8px',
    border: '1px solid rgba(3, 169, 244, 0.2)',
    marginTop: '16px'
  },
  infoNumberCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#03a9f4',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
    marginRight: '12px'
  },
  importInfoText: {
    fontSize: '14px',
    color: '#455a64',
    fontWeight: 500
  },
  cellError: {
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    color: '#c62828',
    fontWeight: 600
  },
  missingValue: {
    color: '#f44336',
    fontStyle: 'italic',
    fontWeight: 600,
    fontSize: '12px'
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(244, 67, 54, 0.07)',
    border: '1px solid rgba(244, 67, 54, 0.3)',
    borderRadius: '8px',
    margin: '0 0 16px'
  },
  warningIcon: {
    color: '#f44336',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '1px'
  },
  warningText: {
    fontSize: '13px',
    color: '#b71c1c',
    lineHeight: 1.6
  },
  cellDuplicate: {
    backgroundColor: 'rgba(255, 152, 0, 0.12)',
    color: '#e65100',
    fontWeight: 600
  },
  duplicateBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(255, 152, 0, 0.08)',
    border: '1px solid rgba(255, 152, 0, 0.4)',
    borderRadius: '8px',
    margin: '0 0 16px'
  },
  duplicateIcon: {
    color: '#e65100',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '1px'
  },
  duplicateText: {
    fontSize: '13px',
    color: '#4e2600',
    lineHeight: 1.6
  },
  duplicateBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 152, 0, 0.2)',
    color: '#e65100',
    fontWeight: 700,
    fontSize: '11px',
    border: '1px solid rgba(255, 152, 0, 0.5)',
    whiteSpace: 'nowrap'
  },
  summaryBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  buttonBox: {
    marginTop: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    padding: '16px 0'
  },
  okButton: {
    padding: '8px 40px',
    fontWeight: 600
  },
  formatHeaderCell: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #6f6f6f'
  },
  summaryContainer: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  summaryTitle: {
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: '8px',
    textAlign: 'center'
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px'
  },
  summaryIconSuccess: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    backgroundColor: 'rgba(46, 204, 113, 0.15)',
    border: '2px solid #2ecc71',
    flexShrink: 0,
    padding: '4px'
  },
  summaryIconSkipped: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    backgroundColor: 'rgba(3, 169, 244, 0.15)',
    border: '2px solid #03a9f4',
    flexShrink: 0,
    padding: '4px'
  },
  summaryIconError: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    backgroundColor: 'rgba(244, 67, 54, 0.15)',
    border: '2px solid #f44336',
    flexShrink: 0,
    padding: '4px'
  },
  summaryNumber: {
    marginBottom: '4px',
    fontWeight: 600,
    color: '#2c3e50',
    display: 'block',
    width: '100%',
    textAlign: 'center'
  },
  summaryTextContainer: {
    flex: 1
  },
  summaryText: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#455a64'
  },
  // New styles for dropdown menu and confirmation dialog
  importButtonContainer: {
    position: 'relative'
  },
  importDropdownButton: {
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 20px',
    minWidth: '160px'
  },
  menuItem: {
    padding: '12px 16px',
    minWidth: '280px',
    '&:hover': {
      backgroundColor: 'rgba(3, 169, 244, 0.08)'
    }
  },
  menuItemTitle: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#2c3e50',
    marginBottom: '4px'
  },
  menuItemDescription: {
    fontSize: '12px',
    color: '#757575',
    lineHeight: 1.4
  },
  confirmationDialogPaper: {
    borderRadius: '12px',
    overflow: 'hidden'
  },
  confirmationDialogTitle: {
    borderBottom: '1px solid #c2c2c2',
    padding: '16px 24px'
  },
  confirmationTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  confirmationWarningIcon: {
    color: '#ff9800',
    fontSize: '24px'
  },
  confirmationTitle: {
    fontWeight: 600,
    color: '#e65100'
  },
  confirmationDialogContent: {
    padding: '20px 24px'
  },
  confirmationMessage: {
    fontSize: '16px',
    color: '#2c3e50',
    marginBottom: '16px',
    lineHeight: 1.5
  },
  confirmationWarningBox: {
    backgroundColor: 'rgba(161,255,246,0.1)',
    borderLeft: '4px solid #16cbd9',
    padding: '12px 16px',
    borderRadius: '4px',
    marginBottom: '16px'
  },
  confirmationWarning: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#578386',
    lineHeight: 1.5
  },
  confirmationQuestion: {
    fontSize: '16px',
    color: '#2c3e50',
    fontWeight: 500
  },
  confirmationDialogActions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  confirmationCancelButton: {
    marginRight: '12px',
    textTransform: 'none'
  },
  confirmationConfirmButton: {
    textTransform: 'none',
    fontWeight: 600
  }
}))
